import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Loader2, AlertCircle } from 'lucide-react'
import { useResponsesStore } from '@/stores/responses-store'
import { useDraftingStore } from '@/stores/drafting-store'
import { useAgentStore } from '@/stores/agent-store'
import { useSectionAssignmentsStore, type CollaborationStatus } from '@/stores/section-assignments-store'
import { useAuthStore } from '@/stores/auth-store'
import { useBidReadOnly } from '@/hooks/useViewOnly'
import { QuestionNavigator } from '@/components/questionnaire/QuestionNavigator'
import { BriefButton } from '@/components/drafting/BriefButton'
import { NeedsFromYouBadge } from '@/components/pack/NeedsFromYouBadge'
import { BriefModal } from '@/components/drafting/BriefModal'
import { EditorFlagBanner } from '@/components/drafting/EditorFlagBanner'
import { useSectionFlagsStore, mergeFlagsBySection } from '@/stores/section-flags-store'
import { useCoverageStore } from '@/stores/coverage-store'
import { QuestionBlock } from '@/components/questionnaire/QuestionBlock'
import { DraftEditor } from '@/components/drafting/DraftEditor'
import { UnifiedAiPanel } from '@/components/drafting/UnifiedAiPanel'
import type { CommentJumpContext } from '@/components/drafting/CommentCard'
import { ResizeHandle } from '@/components/ui/ResizeHandle'
import { useElementWidth } from '@/hooks/useElementWidth'
import { useCommentDeepLink } from '@/hooks/useCommentDeepLink'
import { useCommentsPolling } from '@/hooks/useCommentsPolling'
import { useBidMembers } from '@/hooks/useBidMembers'
import {
  resolvePaneWidths,
  maxAiPanelWidthFor,
  maxOutlineWidthFor,
  MIN_AI_PANEL_WIDTH,
  DEFAULT_AI_PANEL_WIDTH,
  MIN_OUTLINE_WIDTH,
  DEFAULT_OUTLINE_WIDTH,
} from '@/lib/pane-layout'
import type { QuestionnaireItem } from '@/types/questionnaire-types'
import type { OutlineSection } from '@/lib/api'
import { toast } from 'sonner'
import { toastApiError } from '@/lib/api'

// ===================
// Constants
// ===================

// Pane sizing rules live in @/lib/pane-layout — shared with the section and
// document editors, which run the same three-pane shell.
const RESIZE_HANDLE_WIDTH = 4 // ResizeHandle is w-1

// ===================
// Main Component
// ===================

export function QuestionEditorPage() {
  const { bidId, artifactId, sectionKey } = useParams<{
    bidId: string
    artifactId: string
    sectionKey: string
  }>()
  const navigate = useNavigate()
  const { search } = useLocation()

  // Consume `?commentId=` deep links from comment notifications.
  useCommentDeepLink(artifactId ?? '')
  useCommentsPolling(bidId, artifactId)

  // One question per editor — same single-section rule as the section editor.
  const commentJumpContext = useMemo<CommentJumpContext>(
    () => ({
      editorMode: 'single',
      currentSectionKey: sectionKey ?? null,
      buildSectionPath: (key) =>
        `/app/bids/${bidId ?? ''}/responses/${artifactId ?? ''}/questionnaire/${key}`,
      // v7's navigate returns a promise; the context is fire-and-forget.
      navigate: (path) => void navigate(path),
    }),
    [bidId, artifactId, sectionKey, navigate],
  )

  // Stores
  const {
    selectedArtifact: artifact,
    isLoading: artifactLoading,
    error: artifactError,
    fetchArtifactById,
  } = useResponsesStore()

  const {
    outline,
    isLoadingOutline,
    error: outlineError,
    fetchOutline,
  } = useDraftingStore()

  const {
    isPanelOpen,
    flushDraftSavesBeforeThreadChange,
    reset: resetAgentStore,
    segments,
    initSession,
    loadReadOnlySegments,
  } = useAgentStore()
  // A read-only user — an org viewer, or a member who is not on the bid —
  // reads the answer but never opens an AI session: the session endpoint
  // refuses them, which used to leave the editor empty.
  // Until the role and the bid are known they look like an editor; wait, or
  // the session request 403s.
  const { isReadOnly, isLoaded: isAccessLoaded } = useBidReadOnly(bidId)

  const currentUserId = useAuthStore((s) => s.user?.id)
  const loadFlagsForArtifact = useSectionFlagsStore((s) => s.loadFlagsForArtifact)
  const artifactFlags = useSectionFlagsStore((s) => (artifactId ? s.flagsByArtifact[artifactId] : undefined))
  const {
    assignments,
    isLoading: assignmentsLoading,
    error: assignmentsError,
    currentArtifactId: assignmentsArtifactId,
    fetchAssignments,
    assignSection,
    unassignSection,
    updateCollaborationStatus,
    requestReview,
    submitReview,
  } = useSectionAssignmentsStore()

  // Load assignments — collaboration status lives on assignments, and this page
  // can be reached directly (deep link / reload) without the overview page.
  useEffect(() => {
    if (artifactId) {
      void fetchAssignments(artifactId)
    }
  }, [artifactId, fetchAssignments])

  // Bid members for the assignment picker, @mentions and commenter avatars.
  const bidMembers = useBidMembers(bidId)
  const isOwner = bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')

  // Questions from outline
  const questions = useMemo(
    () => (outline?.sections ?? []) as unknown as QuestionnaireItem[],
    [outline?.sections]
  )
  const currentQuestion = questions.find((q) => q.sectionKey === sectionKey)

  // Addendum-impact flag for the current question (merge-on-read; see store).
  const currentSectionFlag = useMemo(() => {
    if (!sectionKey) return undefined
    return mergeFlagsBySection(artifactFlags ?? []).get(sectionKey)
  }, [artifactFlags, sectionKey])

  // Create synthetic OutlineSection for DraftEditor compatibility
  const syntheticSection: OutlineSection | null = currentQuestion
    ? {
        sectionKey: currentQuestion.sectionKey,
        title: `${currentQuestion.number} ${currentQuestion.prompt.slice(0, 60)}`,
        ...(currentQuestion.wordLimit != null ? { targetWordCount: currentQuestion.wordLimit } : {}),
      }
    : null

  // Compute write permission
  const currentAssignment = assignments.find((a) => a.sectionId === sectionKey)
  const isFinalized = artifact?.status === 'finalized'
  const isInReview = artifact?.status === 'pending_review'
  const isFrozen = isFinalized || isInReview
  const canWrite = useMemo(() => {
    if (isReadOnly || isFrozen) return false
    if (isOwner) return true
    // Unassigned — no row, or a status-only row with a null user (tracks
    // collaboration status only) — any member can write
    if (!currentAssignment || currentAssignment.userId === null) return true
    if (currentAssignment.userId === currentUserId) return true
    return false
  }, [isReadOnly, isFrozen, isOwner, currentAssignment, currentUserId])

  // The panel renders for readers too (comments-only, A0), so layout math keys
  // on isPanelOpen. Only the Ask AI (`write`) tab bootstraps the session, and comments-only
  // mode does not mount it — load segments here whenever it won't render.
  const writeTabWillRender = isPanelOpen && canWrite
  useEffect(() => {
    if (!isAccessLoaded || !bidId || !artifactId || !sectionKey) return
    if (isReadOnly) {
      loadReadOnlySegments(artifactId, sectionKey).catch((err: unknown) => {
        toastApiError(err, "Couldn't load this question")
      })
      return
    }
    if (writeTabWillRender) return
    void initSession(bidId, artifactId, sectionKey, 'unit')
  }, [isAccessLoaded, isReadOnly, writeTabWillRender, bidId, artifactId, sectionKey, initSession, loadReadOnlySegments])

  // R1 UX1: the document's state and the assignee are two different reasons;
  // passing the state as a name read "assigned to This document is under review".
  const lockedReason = isFinalized
    ? 'This questionnaire has been finalised'
    : isInReview
      ? 'This questionnaire is under review'
      : null
  const lockedByUser = !canWrite && currentAssignment
    ? (currentAssignment.fullName || currentAssignment.email)
    : null

  // Collaboration status
  const collaborationStatus: CollaborationStatus | null = currentAssignment?.collaborationStatus ?? null

  // Auto-transition: not_started → in_progress when content appears
  const autoTransitionFiredRef = useRef(false)
  useEffect(() => {
    autoTransitionFiredRef.current = false
  }, [sectionKey])

  useEffect(() => {
    if (autoTransitionFiredRef.current) return
    if (!artifactId || !sectionKey) return
    // Only act once assignments for this artifact have loaded — firing against
    // an empty or stale store could clobber a real status with 'in_progress'.
    if (assignmentsLoading || assignmentsError || assignmentsArtifactId !== artifactId) return
    // Skip optimistic assignments — wait for the POST to complete before auto-transitioning
    if (currentAssignment?.id.startsWith('temp-')) return
    // Unassigned questions count as not_started in progress totals, so a
    // missing assignment row is promoted too (the PATCH creates a status-only entry).
    if ((currentAssignment?.collaborationStatus ?? 'not_started') !== 'not_started') return
    if (!canWrite) return

    const hasDraftContent = (content: string) => content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().length > 0
    const matchingSegment = segments.find((seg) => seg.sectionId === sectionKey)

    if (matchingSegment && hasDraftContent(matchingSegment.content)) {
      autoTransitionFiredRef.current = true
      void updateCollaborationStatus(artifactId, sectionKey, 'in_progress').catch(() => {
        // Non-critical
      })
    }
  }, [artifactId, sectionKey, currentAssignment, canWrite, assignmentsLoading, assignmentsError, assignmentsArtifactId, segments, updateCollaborationStatus])

  // Collaboration status change handler
  const handleCollaborationStatusChange = useCallback((status: CollaborationStatus) => {
    if (!artifactId || !sectionKey) return
    void updateCollaborationStatus(artifactId, sectionKey, status).catch((err) => {
      toastApiError(err, 'Failed to update question status')
    })
  }, [artifactId, sectionKey, updateCollaborationStatus])

  // Review flow handlers
  const handleRequestReview = useCallback((reviewerId: string) => {
    if (!artifactId || !sectionKey) return
    void requestReview(artifactId, sectionKey, reviewerId)
      .then(() => toast.success('Review requested'))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to request review'))
  }, [artifactId, sectionKey, requestReview])

  const handleSubmitReview = useCallback((decision: 'approved' | 'changes_requested', comment?: string) => {
    if (!artifactId || !sectionKey) return
    void submitReview(artifactId, sectionKey, decision, comment)
      .then(() => toast.success(decision === 'approved' ? 'Answer approved' : 'Changes requested'))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to submit review'))
  }, [artifactId, sectionKey, submitReview])

  const isReviewer = currentAssignment?.reviewerId === currentUserId && currentAssignment?.collaborationStatus === 'in_review'

  const latestReviewRound = currentAssignment?.reviewRounds?.length
    ? currentAssignment.reviewRounds[currentAssignment.reviewRounds.length - 1]
    : null

  // Resizable panels
  const [aiPanelWidth, setAiPanelWidth] = useState(() => {
    const saved = localStorage.getItem('ai-panel-width')
    return saved ? parseInt(saved, 10) : DEFAULT_AI_PANEL_WIDTH
  })
  const [outlineWidth, setOutlineWidth] = useState(() => {
    const saved = localStorage.getItem('outline-width')
    return saved ? parseInt(saved, 10) : DEFAULT_OUTLINE_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const [isOutlineResizing, setIsOutlineResizing] = useState(false)
  const { ref: containerRef, element: containerEl, width: containerWidth } = useElementWidth()

  // Keep the answer column readable — the side panes give width back when the
  // window can't afford all three at their stored sizes (F19). Resolved here
  // (not at render time) so the drag caps below see the widths actually on
  // screen rather than the stored ones.
  const handlesWidth = isPanelOpen ? 2 * RESIZE_HANDLE_WIDTH : RESIZE_HANDLE_WIDTH
  const { outlineWidth: renderedOutlineWidth, aiPanelWidth: renderedAiPanelWidth } = resolvePaneWidths({
    containerWidth,
    outlineWidth,
    aiPanelWidth: isPanelOpen ? aiPanelWidth : null,
    handlesWidth,
  })
  const effectiveOutlineWidth = renderedOutlineWidth ?? outlineWidth
  const effectiveAiPanelWidth = renderedAiPanelWidth ?? aiPanelWidth

  // Brief modal — opened from the question header BriefButton.
  const [briefOpen, setBriefOpen] = useState(false)
  const loadCoverage = useCoverageStore((s) => s.load)

  // Fetch data on mount
  useEffect(() => {
    if (artifactId) {
      void fetchArtifactById(artifactId)
      void fetchOutline(artifactId)
      void loadFlagsForArtifact(artifactId)
    }
    if (bidId) void loadCoverage(bidId)
  }, [artifactId, bidId, fetchArtifactById, fetchOutline, loadCoverage, loadFlagsForArtifact])

  // Reset agent state when question changes
  useEffect(() => {
    return () => {
      resetAgentStore()
    }
  }, [artifactId, sectionKey, resetAgentStore])

  // Persist panel widths
  useEffect(() => {
    localStorage.setItem('ai-panel-width', String(aiPanelWidth))
  }, [aiPanelWidth])

  useEffect(() => {
    localStorage.setItem('outline-width', String(outlineWidth))
  }, [outlineWidth])

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  const handleOutlineMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsOutlineResizing(true)
  }, [])

  useEffect(() => {
    const activeResize = isResizing || isOutlineResizing

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerEl) return
      const containerRect = containerEl.getBoundingClientRect()

      if (isResizing) {
        const newWidth = containerRect.right - e.clientX
        // Cap the drag so the answer column keeps its minimum width.
        const max = maxAiPanelWidthFor(containerRect.width, effectiveOutlineWidth, handlesWidth)
        setAiPanelWidth(Math.max(MIN_AI_PANEL_WIDTH, Math.min(max, newWidth)))
      }

      if (isOutlineResizing) {
        const newWidth = e.clientX - containerRect.left
        // Only reserve room for the AI panel when it is actually rendered.
        const max = maxOutlineWidthFor(
          containerRect.width,
          isPanelOpen ? effectiveAiPanelWidth : 0,
          handlesWidth
        )
        setOutlineWidth(Math.max(MIN_OUTLINE_WIDTH, Math.min(max, newWidth)))
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      setIsOutlineResizing(false)
    }

    if (activeResize) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [
    isResizing,
    isOutlineResizing,
    containerEl,
    handlesWidth,
    isPanelOpen,
    effectiveOutlineWidth,
    effectiveAiPanelWidth,
  ])

  // Navigation between questions
  const handleQuestionSelect = (key: string) => {
    if (key === sectionKey) return

    void (async () => {
      if (flushDraftSavesBeforeThreadChange) {
        try {
          await flushDraftSavesBeforeThreadChange()
        } catch (error) {
          toast.error(
            error instanceof Error && error.message
              ? error.message
              : 'Please resolve draft save issues before switching questions.'
          )
          return
        }
      }

      void navigate(`/app/bids/${bidId}/responses/${artifactId}/questionnaire/${key}`)
    })()
  }

  // Loading state
  const isLoading = artifactLoading || isLoadingOutline
  const error = artifactError || outlineError

  if (isLoading && !artifact) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="ghost" onClick={() => { void navigate(`/app/bids/${bidId}/responses/${artifactId}/questionnaire`) }}>
          Back to Questions
        </Button>
      </div>
    )
  }

  if (!currentQuestion) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Question not found</p>
        <Button variant="ghost" onClick={() => {
          // Keep the query string: a stale-question deep link degrades to the
          // questionnaire overview with its `?commentId=` intact.
          void navigate(`/app/bids/${bidId}/responses/${artifactId}/questionnaire${search}`)
        }}>
          Back to Questions
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden" ref={containerRef}>
      {/* Three-pane layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Pane: Question Navigator */}
        <div
          className="bg-card shrink-0 overflow-hidden flex flex-col border-r border-border"
          style={{ width: effectiveOutlineWidth }}
          data-outline-pane="true"
        >
          <QuestionNavigator
            questions={questions}
            selectedSectionKey={sectionKey ?? null}
            onQuestionSelect={handleQuestionSelect}
            assignments={assignments}
            bidMembers={bidMembers}
            isOwner={isOwner}
            isFinalized={isFrozen || isReadOnly}
            onAssignSection={(key, userId) => { if (artifactId) void assignSection(artifactId, key, userId) }}
            onUnassignSection={(key) => { if (artifactId) void unassignSection(artifactId, key) }}
            {...(artifactId ? { artifactId } : {})}
          />
        </div>

        {/* Outline Resize Handle */}
        <ResizeHandle
          onMouseDown={handleOutlineMouseDown}
          isResizing={isOutlineResizing}
          aria-label="Resize outline"
        />

        {/* Center Pane: Answer Editor (question prompt renders below toolbar via slot) */}
        <div className="flex-1 min-w-0 overflow-hidden flex flex-col bg-card" data-editor-pane="true">
          {syntheticSection && (
            <BriefModal
              open={briefOpen}
              onOpenChange={setBriefOpen}
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              sectionKey={sectionKey ?? ''}
              unitTitle={syntheticSection.title}
              scopeKind="unit"
              canWrite={canWrite}
            />
          )}
          {currentSectionFlag && (
            <EditorFlagBanner
              bidId={bidId ?? ''}
              flag={currentSectionFlag}
              readOnly={!canWrite}
            />
          )}
          {/* Answer editor */}
          {syntheticSection && (
            <div className="flex-1 min-h-0">
            <DraftEditor
              key={`${artifactId ?? ''}:${sectionKey ?? ''}`}
              bidId={bidId ?? ''}
              headerStatusSlot={
                <div className="flex items-center gap-2">
                  <NeedsFromYouBadge bidId={bidId} />
                  <BriefButton
                    bidId={bidId ?? ''}
                    artifactId={artifactId ?? ''}
                    sectionKey={sectionKey ?? ''}
                    onOpen={() => setBriefOpen(true)}
                    compact
                  />
                </div>
              }
              artifactId={artifactId ?? ''}
              sectionKey={sectionKey ?? ''}
              section={syntheticSection}
              documentSectionNumber={1}
              canWrite={canWrite}
              lockedByUser={lockedByUser}
              lockedReason={lockedReason}
              collaborationStatus={collaborationStatus}
              onCollaborationStatusChange={handleCollaborationStatusChange}
              isReviewer={isReviewer}
              isOwner={isOwner}
              writerName={currentAssignment?.fullName || currentAssignment?.email || null}
              bidMembers={bidMembers}
              currentUserId={currentUserId}
              latestReviewRound={latestReviewRound ?? undefined}
              onRequestReview={handleRequestReview}
              onSubmitReview={handleSubmitReview}
              placeholderText={"Start writing your answer...\n\nTip: Use the AI panel on the right to draft or refine your response."}
              belowToolbarSlot={<QuestionBlock question={currentQuestion} />}
            />
            </div>
          )}
        </div>

        {/* AI Panel Resize Handle */}
        {isPanelOpen && (
          <ResizeHandle
            onMouseDown={handleMouseDown}
            isResizing={isResizing}
            aria-label="Resize AI panel"
          />
        )}

        {/* Right Pane: AI Panel — comments-only for read-only answers (A0). */}
        {isPanelOpen && (
          <div
            className="border-l shrink-0 overflow-hidden flex flex-col bg-muted/25"
            style={{ width: effectiveAiPanelWidth }}
            data-ai-pane="true"
          >
            <UnifiedAiPanel
              key={`${artifactId ?? ''}:${sectionKey ?? ''}`}
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              sectionKey={sectionKey ?? ''}
              scopeLabel="This question"
              bidMembers={bidMembers}
              commentJumpContext={commentJumpContext}
              commentsOnly={!canWrite}
              reviewerTabs={isReviewer}
            />
          </div>
        )}
      </div>
    </div>
  )
}
